import { Injectable } from "@angular/core";
import { BehaviorSubject, of } from "rxjs";
import { HttpUtilService } from "./http-util.service";
import { EnvironmentService } from "@services/environment.service";
import { catchError, tap } from "rxjs/operators";
import { NotificationsService } from "./notifications.service";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class BatchDetailsService {
  public hasLoadedBatchDetails = false;
  public hasLoadedBatchList = false;
  public selectedBatches = [];
  public queryParameters = "";
  public _batchList = new BehaviorSubject({
    isProcessing: false,
    isLoading: true,
    hasError: false,
    data: [],
    totalCount: 0,
  });
  public _batchDetails = new BehaviorSubject({
    isLoading: true,
    hasError: false,
    data: {
      queued: 0,
      processing: 0,
      skipped: 0,
      failed: 0,
      not_found: 0,
      complete: 0,
      total: 0,
    },
  });
  public isDownloadingCSV;
  public batchList$ = this._batchList.asObservable();
  public batchDetails$ = this._batchDetails.asObservable();
  constructor(
    private httpClient: HttpClient,
    private notification: NotificationsService,
    private http: HttpUtilService,
    private environement: EnvironmentService
  ) {}

  getBatchList$(queryParameters) {
    if (!this.hasLoadedBatchList || queryParameters) {
      this.hasLoadedBatchList = true;
      this.getBatchListNetworkCall(null, queryParameters);
    }
    return this.batchList$;
  }

  getBatchDetails$(forceLoad, showSkipped) {
    if (!this.hasLoadedBatchDetails || forceLoad) {
      this.hasLoadedBatchDetails = true;
      this.getBatchDetailsNetworkCall(showSkipped);
    }
    return this.batchDetails$;
  }

  downloadBatchCSV(url, name) {
    this.httpClient
      .get(url, { responseType: "text" })
      .pipe(catchError((error) => of({ error })))
      .subscribe((response: any) => {
        this.isDownloadingCSV = false;
        if (response.error)
          return this.notification.popNotification(
            "",
            "Error Downloading CSV File"
          );
        const blob = new Blob([response], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = name || "batch-course-download.csv";
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        return this.notification.popNotification(
          "",
          "Downloaded CSV: " + a.download
        );
      });
  }

  getDownloadBatchCsv() {
    this.isDownloadingCSV = true;
    let url = `${this.environement.environment.apiRoot}/status/courses/csv`;
    if (this.queryParameters) url += `?${this.queryParameters}`;
    this.http
      .request("get", url)
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        if (response.error) {
          this.isDownloadingCSV = false;
          return this.notification.popNotification(
            "",
            "Error Downloading CSV: " + response.error
          );
        }
        if (!response?.url) {
          return this.notification.popNotification(
            "",
            "Error Downloading CSV, no url found"
          );
        }
        this.downloadBatchCSV(response?.url, response?.name);
      });
  }

  getBatchListNetworkCall(isProcessing?, queryParameters?) {
    const update = isProcessing ? { isProcessing: true } : { isLoading: true };
    this._batchList.next({
      ...this._batchList.getValue(),
      ...update,
      hasError: false,
    });

    let url = `${this.environement.environment.apiRoot}/status/import-sets`;
    if (queryParameters) url += `?${queryParameters}`;

    this.http
      .request("get", url)
      .pipe(catchError((error) => of({ error })))
      .pipe(
        tap((response) => {
          if (response.error) {
            this._batchList.next({
              isProcessing: false,
              data: [],
              isLoading: false,
              hasError: true,
              totalCount: 0,
            });
          } else {
            this._batchList.next({
              isProcessing: false,
              hasError: false,
              isLoading: false,
              totalCount: response?.[0]?.total_count || 0,
              data: Array.isArray(response)
                ? response
                : !!response
                ? [response]
                : [],
            });
          }
        })
      )
      .subscribe();
  }

  getBatchDetailsNetworkCall(showSkipped, queryParameters?) {
    this._batchDetails.next({
      ...this._batchDetails.getValue(),
      isLoading: true,
      hasError: false,
    });
    let url = `${this.environement.environment.apiRoot}/status/archive`;
    if (queryParameters) url += `?${queryParameters}`;
    this.http
      .request("get", url)
      .pipe(catchError((error) => of({ error })))
      .pipe(
        tap((response) => {
          if (response.error) {
            return this._batchDetails.next({
              ...this._batchDetails.getValue(),
              isLoading: false,
              hasError: true,
            });
          }
          const defaultData = this.getBatchDetailsDefaultData();
          const data = { ...defaultData, ...response };
          const total = this.getTotal(data, showSkipped);
          this._batchDetails.next({
            hasError: false,
            isLoading: false,
            data: { ...data, total },
          });
        })
      )
      .subscribe();
  }

  setEmptyBatchDetails() {
    this._batchDetails.next({
      hasError: false,
      isLoading: false,
      data: this.getBatchDetailsDefaultData(),
    });
  }

  getBatchDetailsDefaultData() {
    return {
      queued: 0,
      not_found: 0,
      processing: 0,
      skipped: 0,
      failed: 0,
      complete: 0,
      total: 0,
    };
  }

  getTotal(data, showSkipped) {
    const fields = [
      "complete",
      "completed",
      "failed",
      "queued",
      "processing",
      "not_found",
    ];
    if (showSkipped) fields.push("skipped");
    return Object.keys(data).reduce(
      (acc, key) => (!fields.includes(key) ? acc : Number(data[key]) + acc),
      0
    );
  }
}
